'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { ELEVATION_Z_INDEX } from '#pplx/frontend/aether/components/internal/elevation/elevation.js';
import { SurfaceContext } from '#pplx/frontend/aether/components/Surface/SurfaceContext.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import { useCallback, useMemo, useState } from 'react';
/**
 * Establishes a portal-target boundary for Aether overlay components. Wrap a subtree in Surface so
 * that Menus, Tooltips, Popovers, and other overlays portal into the Surface DOM node rather than
 * escaping to document.body. Keeps overlays correctly layered inside stacking contexts such as side
 * panels with z-index.
 *
 * @example
 * ```tsx
 * <Surface className="fixed inset-y-0 right-0" elevation="raised">
 *   <SidePanel />
 * </Surface>
 * ```
 *
 * @category components
 * @keywords surface, components, establishes, portal-target, boundary, aether, overlay, wrap,
 *   subtree, that, menus, tooltips, popovers, other, overlays, portal, into, node, rather, than,
 *   escaping, documentbody, keeps, correctly, layered, inside, stacking, contexts, such, side,
 *   panels, with, z-index, popover, tooltip, menu
 * @whenToUse Use Surface to establish a new portal-target boundary for Aether overlay components
 *   (DropdownMenu, Popover, Tooltip, etc.). Wrap a subtree in Surface when it lives inside a
 *   stacking context (e.g., a side panel with z-index) and you need overlays to render within that
 *   context rather than escaping to document.body. Surface provides portal-target containment; when
 *   elevation is provided, it applies the matching Aether z-index tier to the wrapping div and
 *   passes that tier through context so DropdownMenu, Popover, and Tooltip can add overlay-tier
 *   z-index relative to the nearest Surface.
 * @guidelineDo
 *   - Wrap subtrees that live inside stacking contexts (e.g., side panels with z-index, modal
 *     content areas) in a Surface so that overlay components (DropdownMenu, Popover, Tooltip) portal
 *     into the correct DOM location
 *   - Use the className and style props to apply positioning and sizing to the wrapping div —
 *     Surface is transparent to visual styling beyond what you explicitly provide
 *   - Use the useNearestSurface hook when building custom overlay components that need to find the
 *     nearest portal target
 *   - Set elevation when the Surface needs an Aether z-index tier; DropdownMenu, Popover, and
 *     Tooltip use the nearest Surface elevation for overlay z-index calculation
 *   - Place a Surface around content areas where overlays should be visually and spatially
 *     contained, such as slide-over panels or embedded iframes
 * @guidelineDont
 *   - Don't use Surface at the top level of the application — AetherProvider already establishes the
 *     root portal context; Surface is for creating additional portal boundaries within subtrees
 *   - Don't nest Surfaces unless you intentionally want overlays in an inner subtree to portal into
 *     the inner Surface rather than the outer one — the nearest Surface wins
 *   - Don't confuse Surface with a visual container — it renders a plain div with no default visual
 *     treatment beyond the className, style, and optional elevation you explicitly provide
 *   - Don't use elevation as an arbitrary layering escape hatch; choose the smallest semantic tier
 *     that matches the container and keep visual styling explicit via className/style
 * @accessibility
 *   - Surface renders a plain div element — it adds no ARIA roles or attributes, keeping the
 *     accessibility tree unaffected
 *   - Overlays portaled into a Surface remain in the correct DOM order relative to their trigger
 *     elements, which helps screen readers maintain logical reading order
 *   - The ref callback uses a state-based pattern (useState + useCallback) to trigger a re-render
 *     once the DOM node is available, ensuring children reading the SurfaceContext get the actual
 *     element rather than null
 *   - When overlays portal into a Surface instead of document.body, they inherit the Surface's
 *     stacking context, preventing z-index conflicts that could obscure content for all users
 * @relatedComponents
 *   - popover
 *   - tooltip
 *   - menu
 */
export function Surface({ ref, children, className, style, elevation }) {
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    // We need state to trigger a re-render once the ref is attached so that
    // children reading the context get the actual DOM element instead of null.
    const [element, setElement] = useState(null);
    const refCallback = useCallback((node) => {
        // A `display: contents` Surface has no box, so it's a degenerate collision
        // boundary/portal target — report the nearest boxed ancestor instead.
        let boxed = node;
        while (boxed &&
            boxed.ownerDocument.defaultView?.getComputedStyle(boxed).display === 'contents') {
            boxed = boxed.parentElement;
        }
        setElement(boxed);
        // Forward the wrapper node to the external ref when provided
        if (typeof ref === 'function') {
            ref(node);
        }
        else if (ref) {
            ref.current = node;
        }
    }, [ref]);
    const mergedStyle = useMemo(() => {
        if (shouldUseActivationOrder || elevation === undefined) {
            return style;
        }
        return { ...style, zIndex: ELEVATION_Z_INDEX[elevation] };
    }, [style, elevation, shouldUseActivationOrder]);
    // An inline value object re-rendered every overlay (Tooltip, Popover, Menu)
    // inside the surface each time the surface re-rendered, e.g. on every
    // streamed token in a thread.
    const surfaceContextValue = useMemo(() => ({ portalTargetElement: element, elevation }), [element, elevation]);
    return (_jsx("div", { ref: refCallback, className: className, style: mergedStyle, children: _jsx(SurfaceContext.Provider, { value: surfaceContextValue, children: children }) }));
}
//# sourceMappingURL=index.js.map